import React, { useState, useEffect } from 'react';
import { Music, Tent, CupSoda, Camera, PartyPopper, ShieldCheck, CheckCircle2, ArrowRight, Calendar, MapPin, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { partyData, features, timeline, faqs } from '../mock';

const iconMap = {
  'music': Music,
  'tent': Tent,
  'cup-soda': CupSoda,
  'camera': Camera,
  'party-popper': PartyPopper,
  'shield-check': ShieldCheck
};

const Home = () => {
  const [ticketCount, setTicketCount] = useState(partyData.ticketsSold);
  const [daysUntilParty, setDaysUntilParty] = useState(0);

  useEffect(() => {
    // Calculate days until party
    const partyDate = new Date(partyData.partyDate);
    const today = new Date();
    const diffTime = partyDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysUntilParty(diffDays);
  }, []);

  const progressPercentage = (ticketCount / partyData.ticketsRequired) * 100;
  const isPartyConfirmed = ticketCount >= partyData.ticketsRequired;

  const handleBuyTicket = () => {
    // Mock ticket purchase
    if (ticketCount < partyData.ticketsTotal) {
      setTicketCount(prev => prev + 1);
      alert('Ticket purchase simulated! (This will use real Stripe in production)');
    } else {
      alert('Sorry, all tickets are sold out!');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/25">
        <div className="max-w-7xl mx-auto px-6 lg:px-20 py-5 flex items-center justify-between">
          <div className="text-2xl font-bold" style={{ color: '#00FFD1' }}>RIGA TEENS</div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#offer" className="text-white/60 hover:text-white transition-colors text-lg">What You Get</a>
            <a href="#how" className="text-white/60 hover:text-white transition-colors text-lg">How It Works</a>
            <a href="#faq" className="text-white/60 hover:text-white transition-colors text-lg">FAQ</a>
          </nav>
          <Button 
            onClick={handleBuyTicket}
            className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] font-medium px-6 py-3 h-auto transition-all duration-400"
            style={{ borderRadius: '0px' }}
          >
            Get Ticket
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Finally. A Real Party for Riga Teens.
          </h1>
          <p className="text-xl md:text-2xl text-white/85 mb-12 max-w-3xl mx-auto">
            No sketchy clubs. No fake IDs. Just 80 of us, one night, pure vibes. November 29th.
          </p>
          
          <Button 
            onClick={handleBuyTicket}
            className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] font-medium text-xl px-8 py-6 h-auto transition-all duration-400 mb-4 animate-pulse"
            style={{ borderRadius: '0px' }}
          >
            Get Your Ticket - {partyData.ticketPrice} EUR
            <ArrowRight className="ml-2" size={24} />
          </Button>
          
          <div className="flex items-center justify-center gap-2 text-white/85">
            <CheckCircle2 size={20} style={{ color: '#00FFD1' }} />
            <span className="text-lg">100% refund if we don't hit 35 tickets</span>
          </div>
        </div>
      </section>

      {/* Progress Bar Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/10 border-white/25 p-8 md:p-12" style={{ borderRadius: '0px' }}>
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                {ticketCount}/{partyData.ticketsRequired} tickets sold - 
                {isPartyConfirmed ? ' Party is LOCKED IN!' : ' Party is happening!'}
              </h2>
              <p className="text-xl text-white">
                Once we hit 35 tickets, the party is LOCKED IN
              </p>
            </div>
            
            <div className="relative w-full h-6 bg-black border border-white/40 overflow-hidden mb-4" style={{ borderRadius: '0px' }}>
              <div 
                className="absolute top-0 left-0 h-full transition-all duration-1000 ease-out"
                style={{ 
                  width: `${progressPercentage}%`,
                  background: '#00FFD1'
                }}
              />
            </div>
            
            <div className="flex justify-between text-sm text-white/60">
              <span>0 tickets</span>
              <span className="font-bold" style={{ color: '#00FFD1' }}>35 tickets (minimum)</span>
              <span>80 tickets (max)</span>
            </div>
          </Card>
        </div>
      </section>

      {/* The Offer Section */}
      <section id="offer" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">What You Get</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card 
                  key={index} 
                  className="bg-white/10 border-white/25 p-8 hover:bg-white/5 transition-all duration-400"
                  style={{ borderRadius: '0px' }}
                >
                  <IconComponent size={48} className="mb-4" style={{ color: '#00FFD1' }} />
                  <h3 className="text-2xl font-bold mb-3 text-white">{feature.title}</h3>
                  <p className="text-white text-lg">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how" className="py-20 px-6 bg-[#121212]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">How It Works</h2>
          
          <div className="space-y-6">
            {timeline.map((item, index) => (
              <Card 
                key={index}
                className="bg-black border-white/25 p-8 flex items-start gap-6 hover:border-[#00FFD1]/50 transition-all duration-400"
                style={{ borderRadius: '0px' }}
              >
                <div 
                  className="flex-shrink-0 w-16 h-16 flex items-center justify-center text-2xl font-bold border-2"
                  style={{ 
                    borderColor: '#00FFD1',
                    color: '#00FFD1',
                    borderRadius: '0px'
                  }}
                >
                  {item.step}
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2 text-white">{item.title}</h3>
                  <p className="text-white text-lg">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white/10 border-white/25 p-8 md:p-12 text-center" style={{ borderRadius: '0px' }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-8">Organized by Riga teens, for Riga teens</h2>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center justify-center gap-3">
                <ShieldCheck size={24} style={{ color: '#00FFD1' }} />
                <span className="text-xl">Your money is protected by Stripe + full refund guarantee</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <CheckCircle2 size={24} style={{ color: '#00FFD1' }} />
                <span className="text-xl">Payment options: Stripe, Bank Transfer, Crypto</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Mail size={24} style={{ color: '#00FFD1' }} />
                <span className="text-xl">Questions? Email {partyData.contactEmail}</span>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-[#121212]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">Frequently Asked Questions</h2>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-black border border-white/25 px-6"
                style={{ borderRadius: '0px' }}
              >
                <AccordionTrigger className="text-xl font-semibold text-left hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-lg text-white/85 pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <div className="flex items-center justify-center gap-4 mb-4">
              <Calendar size={32} style={{ color: '#00FFD1' }} />
              <span className="text-3xl font-bold">Party date: {daysUntilParty} days away</span>
            </div>
            <div className="flex items-center justify-center gap-4">
              <MapPin size={32} style={{ color: '#00FFD1' }} />
              <span className="text-2xl text-white/85">{partyData.venue}</span>
            </div>
          </div>

          <Button 
            onClick={handleBuyTicket}
            className="bg-[#00FFD1] text-black hover:bg-[#00FFD1]/10 hover:text-[#00FFD1] font-bold text-2xl px-12 py-8 h-auto transition-all duration-400 mb-6"
            style={{ borderRadius: '0px' }}
          >
            Secure My Spot - {partyData.ticketPrice} EUR
            <ArrowRight className="ml-3" size={28} />
          </Button>
          
          <p className="text-xl text-white/85 font-medium">
            Limited to {partyData.ticketsTotal} people. First come, first served.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/25 py-8 px-6">
        <div className="max-w-7xl mx-auto text-center text-white/60">
          <p className="mb-2">© 2024 Riga Teens Party. All rights reserved.</p>
          <p>Contact: {partyData.contactEmail}</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;